import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowClockwise,
  CaretDown,
  Check,
  CircleNotch,
  FilePdf,
  MagnifyingGlass,
  Plus,
  Trash,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  EditorForm,
  Field,
  inputClasses,
  textareaClasses,
  type EditorSectionProps,
} from './shared';
import { aboutSchema, type AboutValues } from '../schemas';
import { uploadFile } from '@/lib/api/uploadFile';
import { SKILL_CATEGORIES } from './skills-catalog';

const SKILLS_MAX = 20;

export const ABOUT_SEED: AboutValues = {
  body: '',
  skills: [],
  resumeUrl: '',
};

const RESUME_MAX_BYTES = 10 * 1024 * 1024;

export function AboutEditor({
  initial,
  onDirty,
  onSubmit,
  submitting,
  onDraftChange,
}: EditorSectionProps<AboutValues>) {
  const form = useForm<AboutValues>({
    resolver: zodResolver(aboutSchema),
    defaultValues: { ...ABOUT_SEED, ...initial },
  });

  useEffect(() => {
    onDraftChange?.(form.getValues());
    const sub = form.watch((v) => {
      onDirty();
      onDraftChange?.(v);
    });
    return () => sub.unsubscribe();
  }, [form, onDirty, onDraftChange]);

  const { errors } = form.formState;

  return (
    <EditorForm onSubmit={form.handleSubmit(async (v) => onSubmit(v))} submitting={submitting}>
      <Field label="Bio" hint="Keep it under 400 characters. Write like you speak." error={errors.body?.message}>
        <textarea
          {...form.register('body')}
          rows={6}
          placeholder="Introduce yourself in a couple of sentences."
          className={textareaClasses}
        />
      </Field>

      <Field label="Skills" hint={`Pick from the list. Up to ${SKILLS_MAX}.`}>
        <Controller
          control={form.control}
          name="skills"
          render={({ field }) => (
            <SkillsPicker value={field.value} onChange={field.onChange} />
          )}
        />
      </Field>

      <Field
        label="Résumé"
        hint="Optional. Upload a PDF or link to one. Renders as a download button on your portfolio."
        error={errors.resumeUrl?.message}
      >
        <Controller
          control={form.control}
          name="resumeUrl"
          render={({ field }) => (
            <ResumeInput value={field.value ?? ''} onChange={field.onChange} />
          )}
        />
      </Field>
    </EditorForm>
  );
}

function ResumeInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [mode, setMode] = useState<'upload' | 'url'>(value ? 'url' : 'upload');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function handleFile(file: File) {
    if (file.size > RESUME_MAX_BYTES) {
      toast.error('File too large', { description: 'Max 10 MB.' });
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      toast.error('Wrong file type', { description: 'Upload a PDF.' });
      return;
    }
    setBusy(true);
    try {
      const { url } = await uploadFile(file, 'resume');
      onChange(url);
    } catch (e) {
      toast.error('Upload failed', {
        description: e instanceof Error ? e.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  if (value) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 rounded-lg border border-line bg-board px-3 py-2.5">
          <FilePdf size={18} weight="duotone" className="shrink-0 text-ink" />
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="flex-1 truncate font-mono text-xs text-ink-muted underline-offset-2 hover:underline"
          >
            {value}
          </a>
        </div>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail disabled:opacity-60"
          >
            {busy ? <CircleNotch size={12} className="animate-spin" /> : <ArrowClockwise size={12} />}
            {busy ? 'Uploading…' : 'Replace'}
          </button>
          <button
            type="button"
            onClick={() => onChange('')}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink-muted hover:border-red-300 hover:text-red-700"
          >
            <Trash size={12} /> Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 inline-flex rounded-md border border-line bg-board p-0.5 text-xs">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={
            mode === 'upload'
              ? 'inline-flex items-center gap-1.5 rounded bg-rail px-3 py-1.5 font-medium text-ink'
              : 'inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium text-ink-muted'
          }
        >
          <UploadSimple size={12} /> Upload
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={
            mode === 'url'
              ? 'inline-flex items-center gap-1.5 rounded bg-rail px-3 py-1.5 font-medium text-ink'
              : 'inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium text-ink-muted'
          }
        >
          URL
        </button>
      </div>
      {mode === 'upload' ? (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-board py-8 text-center hover:border-ink disabled:opacity-60"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-canvas text-ink-muted">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
            </span>
            <span className="text-sm font-medium text-ink">
              {busy ? 'Uploading…' : 'Click to upload a PDF'}
            </span>
            <span className="text-[11px] text-ink-faint">PDF · Max 10 MB</span>
          </button>
        </>
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…/resume.pdf"
          className={inputClasses}
        />
      )}
    </div>
  );
}

function SkillsPicker({
  value,
  onChange,
}: {
  value: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => searchRef.current?.focus(), 10);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open]);

  const selected = new Set(value);
  const atCap = value.length >= SKILLS_MAX;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SKILL_CATEGORIES.map((c) => ({ label: c.label, skills: c.skills.slice() }));
    return SKILL_CATEGORIES.map((c) => ({
      label: c.label,
      skills: c.skills.filter((s) => s.toLowerCase().includes(q)),
    })).filter((c) => c.skills.length > 0);
  }, [query]);

  function toggle(skill: string) {
    if (selected.has(skill)) {
      onChange(value.filter((s) => s !== skill));
    } else if (!atCap) {
      onChange([...value, skill]);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <div className="flex min-h-[44px] flex-wrap items-center gap-2 rounded-md border border-line bg-board p-2">
        {value.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-rail px-2.5 py-1 text-xs text-ink"
          >
            {s}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== s))}
              aria-label={`Remove ${s}`}
              className="text-ink-faint hover:text-ink"
            >
              <X size={10} weight="bold" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-line bg-canvas px-2.5 py-1 text-xs font-medium text-ink-muted hover:border-ink hover:text-ink"
        >
          <Plus size={12} /> Add skill
          <CaretDown size={10} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        </button>
        <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
          {value.length}/{SKILLS_MAX}
        </span>
      </div>

      {open ? (
        <div className="absolute left-0 right-0 z-20 mt-2 overflow-hidden rounded-lg border border-line bg-canvas shadow-lg">
          <div className="flex items-center gap-2 border-b border-line bg-board px-3 py-2.5">
            <MagnifyingGlass size={14} className="text-ink-faint" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search skills"
              className="flex-1 bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="text-ink-faint hover:text-ink"
              >
                <X size={12} weight="bold" />
              </button>
            ) : null}
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-ink-faint">No skills match.</p>
            ) : (
              filtered.map((category) => (
                <div key={category.label} className="mb-2 last:mb-0">
                  <p className="px-2 pb-1 pt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-faint">
                    {category.label}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {category.skills.map((skill) => {
                      const isSelected = selected.has(skill);
                      const disabled = !isSelected && atCap;
                      return (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => toggle(skill)}
                          disabled={disabled}
                          className={
                            isSelected
                              ? 'inline-flex items-center gap-1.5 rounded-full border border-ink bg-ink px-2.5 py-1 text-xs text-board'
                              : disabled
                              ? 'inline-flex items-center gap-1.5 rounded-full border border-line bg-board px-2.5 py-1 text-xs text-ink-faint opacity-60'
                              : 'inline-flex items-center gap-1.5 rounded-full border border-line bg-board px-2.5 py-1 text-xs text-ink hover:border-ink'
                          }
                        >
                          {isSelected ? <Check size={10} weight="bold" /> : null}
                          {skill}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
          {atCap ? (
            <div className="border-t border-line bg-board px-3 py-2 text-[11px] text-ink-muted">
              You've reached the {SKILLS_MAX}-skill limit. Remove one to add another.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
